/**
 * Which brand a link belongs to, worked out from its host so the code can carry that platform's logo without asking the visitor which platform they pasted. Recognition and artwork
 * are separate concerns, deliberately. A platform appears here as soon as we can identify its links; whether we hold a logo for it is answered by `PLATFORM_ICON_FILE_NAMES` alone.
 */
export enum QRSocialPlatform {
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  LINKEDIN = 'linkedin',
  WHATSAPP = 'whatsapp',
  YOUTUBE = 'youtube',
  SNAPCHAT = 'snapchat',
  TIKTOK = 'tiktok',
  X = 'x',
  PINTEREST = 'pinterest',
  REDDIT = 'reddit',
  TWITCH = 'twitch',
  BEHANCE = 'behance',
  DRIBBBLE = 'dribbble',
  LINE = 'line',
  THREADS = 'threads',
  MESSENGER = 'messenger',
  SPOTIFY = 'spotify',
  TELEGRAM = 'telegram',
  DISCORD = 'discord',
  GOOGLE_MAPS = 'google-maps',
  GOOGLE_BUSINESS_PROFILE = 'google-business-profile',
  PAYPAL = 'paypal',
  CASH_APP = 'cash-app',
  VENMO = 'venmo',
  STRIPE = 'stripe',
  SQUARE = 'square',
}

/**
 * How each platform is written when a sentence names it. Brand names, so they are not translated — the surrounding sentence is. Complete rather than partial: a platform we can
 * recognise can always be named, even where we hold no logo for it.
 */
export const QR_SOCIAL_PLATFORM_NAMES: Record<QRSocialPlatform, string> = {
  [QRSocialPlatform.FACEBOOK]: 'Facebook',
  [QRSocialPlatform.INSTAGRAM]: 'Instagram',
  [QRSocialPlatform.LINKEDIN]: 'LinkedIn',
  [QRSocialPlatform.WHATSAPP]: 'WhatsApp',
  [QRSocialPlatform.YOUTUBE]: 'YouTube',
  [QRSocialPlatform.SNAPCHAT]: 'Snapchat',
  [QRSocialPlatform.TIKTOK]: 'TikTok',
  [QRSocialPlatform.X]: 'X',
  [QRSocialPlatform.PINTEREST]: 'Pinterest',
  [QRSocialPlatform.REDDIT]: 'Reddit',
  [QRSocialPlatform.TWITCH]: 'Twitch',
  [QRSocialPlatform.BEHANCE]: 'Behance',
  [QRSocialPlatform.DRIBBBLE]: 'Dribbble',
  [QRSocialPlatform.LINE]: 'LINE',
  [QRSocialPlatform.THREADS]: 'Threads',
  [QRSocialPlatform.MESSENGER]: 'Messenger',
  [QRSocialPlatform.SPOTIFY]: 'Spotify',
  [QRSocialPlatform.TELEGRAM]: 'Telegram',
  [QRSocialPlatform.DISCORD]: 'Discord',
  [QRSocialPlatform.GOOGLE_MAPS]: 'Google Maps',
  [QRSocialPlatform.GOOGLE_BUSINESS_PROFILE]: 'Google Business Profile',
  [QRSocialPlatform.PAYPAL]: 'PayPal',
  [QRSocialPlatform.CASH_APP]: 'Cash App',
  [QRSocialPlatform.VENMO]: 'Venmo',
  [QRSocialPlatform.STRIPE]: 'Stripe',
  [QRSocialPlatform.SQUARE]: 'Square',
};

const ICON_FILE_EXTENSION = '.png';
const HOST_LABEL_SEPARATOR = '.';

/**
 * The file each platform's mark is served from, and the single answer to "do we have a logo for this?". A platform absent from this map is still recognised — its link simply
 * carries no logo, which is the honest outcome rather than a broken image in the middle of a QR code.
 */
const PLATFORM_ICON_FILE_NAMES: Partial<Record<QRSocialPlatform, string>> = {
  [QRSocialPlatform.FACEBOOK]: `social-icon-facebook${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.INSTAGRAM]: `social-icon-instagram${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.LINKEDIN]: `social-icon-linkedin${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.WHATSAPP]: `social-icon-whatsapp${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.YOUTUBE]: `social-icon-youtube${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.SNAPCHAT]: `social-icon-snapchat${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.TIKTOK]: `social-icon-tiktok${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.PINTEREST]: `social-icon-pinterest${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.REDDIT]: `social-icon-reddit${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.TWITCH]: `social-icon-twitch${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.BEHANCE]: `social-icon-behance${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.DRIBBBLE]: `social-icon-dribbble${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.LINE]: `social-icon-line${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.X]: `social-icon-x${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.THREADS]: `social-icon-threads${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.GOOGLE_MAPS]: `social-icon-google-maps${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.GOOGLE_BUSINESS_PROFILE]: `social-icon-google-business-profile${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.MESSENGER]: `social-icon-messenger${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.SPOTIFY]: `social-icon-spotify${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.TELEGRAM]: `social-icon-telegram${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.DISCORD]: `social-icon-discord${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.PAYPAL]: `social-icon-paypal${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.CASH_APP]: `social-icon-cash-app${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.VENMO]: `social-icon-venmo${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.STRIPE]: `social-icon-stripe${ICON_FILE_EXTENSION}`,
  [QRSocialPlatform.SQUARE]: `social-icon-square${ICON_FILE_EXTENSION}`,
};

/**
 * Marks a poster item's logo as naming one of our own platform icons rather than something the visitor uploaded. The colon is what makes that unambiguous: a hashed filename is
 * `bin2hex(random_bytes(16))`, so 32 lowercase hex characters and nothing else, and no upload past or future can produce one of these.
 */
const PLATFORM_LOGO_REFERENCE_PREFIX = 'social:';

/** Every platform keyed by its own slug, so a reference can be read back without restating the list. */
const PLATFORM_BY_SLUG: Record<string, QRSocialPlatform | undefined> = Object.fromEntries(
  Object.values(QRSocialPlatform).map((platform): [string, QRSocialPlatform] => {
    return [platform, platform];
  })
);

/**
 * Keyed on the registrable host each platform serves links from, short links included. Matched against the host's own suffixes rather than by substring, so
 * `facebook.com.evil.example` — the shape a phishing link takes — is never dressed up in someone else's logo.
 */
const PLATFORM_BY_HOST: Record<string, QRSocialPlatform> = {
  'facebook.com': QRSocialPlatform.FACEBOOK,
  'fb.com': QRSocialPlatform.FACEBOOK,
  'fb.me': QRSocialPlatform.FACEBOOK,
  'instagram.com': QRSocialPlatform.INSTAGRAM,
  'instagr.am': QRSocialPlatform.INSTAGRAM,
  'linkedin.com': QRSocialPlatform.LINKEDIN,
  'lnkd.in': QRSocialPlatform.LINKEDIN,
  'whatsapp.com': QRSocialPlatform.WHATSAPP,
  'wa.me': QRSocialPlatform.WHATSAPP,
  'youtube.com': QRSocialPlatform.YOUTUBE,
  'youtu.be': QRSocialPlatform.YOUTUBE,
  'snapchat.com': QRSocialPlatform.SNAPCHAT,
  'tiktok.com': QRSocialPlatform.TIKTOK,
  'x.com': QRSocialPlatform.X,
  'twitter.com': QRSocialPlatform.X,
  't.co': QRSocialPlatform.X,
  'pinterest.com': QRSocialPlatform.PINTEREST,
  'pin.it': QRSocialPlatform.PINTEREST,
  'reddit.com': QRSocialPlatform.REDDIT,
  'redd.it': QRSocialPlatform.REDDIT,
  'twitch.tv': QRSocialPlatform.TWITCH,
  'behance.net': QRSocialPlatform.BEHANCE,
  'dribbble.com': QRSocialPlatform.DRIBBBLE,
  'line.me': QRSocialPlatform.LINE,
  'threads.net': QRSocialPlatform.THREADS,
  'threads.com': QRSocialPlatform.THREADS,
  'messenger.com': QRSocialPlatform.MESSENGER,
  'm.me': QRSocialPlatform.MESSENGER,
  'spotify.com': QRSocialPlatform.SPOTIFY,
  'spotify.link': QRSocialPlatform.SPOTIFY,
  'telegram.me': QRSocialPlatform.TELEGRAM,
  'telegram.org': QRSocialPlatform.TELEGRAM,
  't.me': QRSocialPlatform.TELEGRAM,
  'discord.com': QRSocialPlatform.DISCORD,
  'discord.gg': QRSocialPlatform.DISCORD,
  'maps.google.com': QRSocialPlatform.GOOGLE_MAPS,
  'maps.app.goo.gl': QRSocialPlatform.GOOGLE_MAPS,
  'g.page': QRSocialPlatform.GOOGLE_BUSINESS_PROFILE,
  'search.google.com': QRSocialPlatform.GOOGLE_BUSINESS_PROFILE,
  'paypal.com': QRSocialPlatform.PAYPAL,
  'paypal.me': QRSocialPlatform.PAYPAL,
  'cash.app': QRSocialPlatform.CASH_APP,
  'venmo.com': QRSocialPlatform.VENMO,
  'stripe.com': QRSocialPlatform.STRIPE,
  'squareup.com': QRSocialPlatform.SQUARE,
  'square.link': QRSocialPlatform.SQUARE,
};

/**
 * Walks the host's own suffixes, longest first, so a map keyed on registrable hosts still recognises `chat.whatsapp.com`, `m.facebook.com` and a plain `www.` prefix without an
 * entry for each.
 */
const getPlatformForHost = (hostname: string): QRSocialPlatform | null => {
  const labels = hostname.toLowerCase().split(HOST_LABEL_SEPARATOR);

  for (let firstLabel = 0; firstLabel < labels.length; firstLabel++) {
    const candidateHost = labels.slice(firstLabel).join(HOST_LABEL_SEPARATOR);
    const platform = PLATFORM_BY_HOST[candidateHost];
    if (platform !== undefined) {
      return platform;
    }
  }

  return null;
};

/**
 * Read from the built payload rather than the raw field, which is what makes a WhatsApp phone number carry the WhatsApp logo: by then the number has already become a `wa.me` link.
 * An unfinished or invalid payload has no host to read, so it simply has no platform.
 */
export const getQRSocialPlatform = (payload: string): QRSocialPlatform | null => {
  try {
    return getPlatformForHost(new URL(payload).hostname);
  } catch {
    return null;
  }
};

/**
 * The platform behind a link as it is being typed, before any payload exists. A visitor half way through a URL has usually typed the host but not the scheme, so a schemeless entry
 * is given the scheme the payload builder would add anyway rather than being read as unrecognised.
 */
export const getQRSocialPlatformFromTypedLink = (link: string): QRSocialPlatform | null => {
  const typedLink = link.trim();
  if (typedLink === '') {
    return null;
  }
  return getQRSocialPlatform(typedLink) ?? getQRSocialPlatform(`https://${typedLink}`);
};

/** `null` for a platform we recognise but hold no artwork for — its code is drawn without a logo. */
export const getQRSocialPlatformIconFileName = (platform: QRSocialPlatform): string | null => {
  return PLATFORM_ICON_FILE_NAMES[platform] ?? null;
};

export const hasQRSocialPlatformIcon = (platform: QRSocialPlatform): boolean => {
  return getQRSocialPlatformIconFileName(platform) !== null;
};

/**
 * What a poster item stores to wear a platform's logo. The icon is ours and already public, so naming it is enough — nothing is copied into the visitor's media for the editor to
 * be able to draw it.
 */
export const getQRPlatformLogoReference = (platform: QRSocialPlatform): string => {
  return `${PLATFORM_LOGO_REFERENCE_PREFIX}${platform}`;
};

/** `null` for an ordinary uploaded logo, which is every value that is not one of ours. */
export const getQRPlatformFromLogoReference = (logoReference: string): QRSocialPlatform | null => {
  if (!logoReference.startsWith(PLATFORM_LOGO_REFERENCE_PREFIX)) {
    return null;
  }
  return PLATFORM_BY_SLUG[logoReference.slice(PLATFORM_LOGO_REFERENCE_PREFIX.length)] ?? null;
};

/** Full-colour brand marks, served by the app rather than the assets CDN — the same path the free QR tool reads them from. */
const PLATFORM_ICON_APP_DIRECTORY = 'images/resources/social-icons/original/';

/**
 * Where one of our platform marks is served from. One size serves every tier, unlike an upload: these are small flat marks, and a QR only ever draws its logo at a fraction of the
 * code's width, so there is no larger variant worth resolving to — which is why this takes a platform and not a screen tier. `null` when we hold no artwork for the platform.
 */
export const getQRPlatformLogoUrl = (platform: QRSocialPlatform): string | null => {
  const iconFileName = getQRSocialPlatformIconFileName(platform);
  if (iconFileName === null) {
    return null;
  }
  return window.PMW.util.asset_url(`${PLATFORM_ICON_APP_DIRECTORY}${iconFileName}`);
};
